import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import { ERoutes } from "@/utils/constants";

/**
 * Shared scroll-direction visibility flag used by both the fixed
 * `SecondHeader` and the portalled `LoggedInBanner`. Returns `true`
 * while the user is scrolling up (or at rest), `false` while scrolling
 * down — except on `/account/*` pages with sidebars (where the header
 * is pinned static so it doesn't collide with the sticky sidebar). The
 * Claims page uses `noSidebar`, so it gets the fade like every other
 * public page.
 */
export const useHeaderFadeVisible = (): boolean => {
  const { pathname } = useLocation();
  const disableFade =
    pathname.startsWith("/account") && !pathname.startsWith(ERoutes.AccountVideos);
  const [visible, setVisible] = useState(true);
  const lastScrollY = useRef(0);

  useEffect(() => {
    if (disableFade) {
      setVisible(true);
      return;
    }
    lastScrollY.current = window.scrollY;
    setVisible(true);

    // Browser scroll restoration + programmatic scrolls fire `scroll`
    // during first paint. Stabilize for 600ms after mount: every scroll
    // event there is treated as noise (baseline re-synced, no direction
    // decision), so the header never hides until the user truly drags.
    let stabilized = false;
    const stabilizeTimer = window.setTimeout(() => {
      stabilized = true;
      lastScrollY.current = window.scrollY;
    }, 600);

    const DIRECTION_THRESHOLD = 5;
    const handleScroll = () => {
      if (!stabilized) {
        lastScrollY.current = window.scrollY;
        return;
      }
      const currentY = window.scrollY;
      const delta = currentY - lastScrollY.current;
      if (Math.abs(delta) < DIRECTION_THRESHOLD) return;
      setVisible(delta < 0);
      lastScrollY.current = currentY;
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.clearTimeout(stabilizeTimer);
      window.removeEventListener("scroll", handleScroll);
    };
  }, [disableFade]);

  return visible;
};
